import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import Money from 'js-money/lib';
import moment from 'moment';

import { orResolver } from '../util/helpers';
import { PERIOD_TYPES } from '../constants/intl';
import { getConsecutiveDays } from '../util/getConsecutiveDays';
import { DATE_WEEKDAY_FORMAT } from '../constants/dates';

const getDisplayName = fromComponent => fromComponent.displayName || fromComponent.name || 'Component';

const DEFAULT_DATE_FORMAT_OPTIONS = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
};

export const generateUtilFunctions = (intl) => {
    const formatMessage = (id, params) => intl.formatMessage({ id }, params);

    const formatDate = (date, options = DEFAULT_DATE_FORMAT_OPTIONS) => new Intl
        .DateTimeFormat(intl.defaultLocale, options)
        .format(new Date(date));

    const getDayName = dayNumber =>
        new Intl.DateTimeFormat(intl.defaultLocale, { weekday: DATE_WEEKDAY_FORMAT }).format(new Date(0, 0, dayNumber));

    const formatPrice = (price) => {
        const money = Money.fromInteger(price);
        const currency = money.getCurrency();
        const decimalPrice = money.toDecimal();

        const formattedPrice = intl.formatNumber(decimalPrice, { style: 'currency', currency })
            .replace(/([,.]00)($|\D+)/, '$2');

        if (currency === 'NOK') {
            return formattedPrice
                .replace(/^(.*)\s(\D+)$/, '$2 $1')
                .trim().split(/\s/)
                .reduce((acc, _, idx, arr) => (idx === 1 ? [acc, arr.slice(1)] : acc))
                .reverse()
                .flat()
                .join(' ');
        }

        return formattedPrice;
    };

    const formatPeriod = (period, options = {}) => {
        const { type = PERIOD_TYPES.NOUN } = options;
        const duration = moment.duration(period);
        const months = Math.round(duration.asMonths());
        const weeks = Math.round(duration.asWeeks());
        const days = Math.round(duration.asDays());

        const periodKey = orResolver({ months, weeks, days })([
            [months === 12, () => 'year'],
            [months === 6, () => 'halfYear'],
            [months === 3, () => 'quarter'],
            [months === 1, () => 'month'],
            [weeks === 1, () => 'week'],
            [days === 1, () => 'day'],
            [true, () => false],
        ]);

        if (periodKey) {
            return formatMessage(`duration.${type}.${periodKey}`);
        }

        const thresholds = {
            // M - least number of months to be considered a year
            ...((months % 12) && { M: Number.MAX_VALUE }), // do not round to full year(s)
        };

        return duration.humanize(thresholds);
    };

    const formatWeekDays = (days = '') => {
        const daysNumbersArray = [...new Set(days.split(''))]
            .filter((day => day >= 1 && day <= 7))
            .map(Number)
            .sort();
        const consecutiveDays = getConsecutiveDays(daysNumbersArray);

        if (consecutiveDays.length > 2) {
            const firstDay = getDayName(consecutiveDays[0]);
            const lastDay = getDayName(consecutiveDays[consecutiveDays.length - 1]);

            return `${firstDay} - ${lastDay}`;
        }

        return daysNumbersArray.map(dayNumber => getDayName(dayNumber)).join(', ');
    };

    return {
        formatMessage,
        formatDate,
        formatPrice,
        formatPeriod,
        formatWeekDays,
    };
};

export default (WrappedComponent, options = {}) => {
    const { withRef = false } = options;

    class IntlEasing extends Component {
        static propTypes = {
            intl: PropTypes.shape({
                formatMessage: PropTypes.func,
                formatDate: PropTypes.func,
                formatNumber: PropTypes.func,
                defaultLocale: PropTypes.string,
            }).isRequired,
        };

        static displayName = `IntlEasing(${getDisplayName(WrappedComponent)})`;

        render() {
            const { intl } = this.props;
            return (
                <WrappedComponent
                    { ...this.props }
                    intl={ intl }
                    i18n={ generateUtilFunctions(intl, this.props) }
                    ref={ withRef ? 'wrappedInstance' : null }
                />
            );
        }
    }

    return injectIntl(IntlEasing);
};

export const propTypes = PropTypes.shape({
    formatMessage: PropTypes.func,
    formatDate: PropTypes.func,
    formatPrice: PropTypes.func,
    formatPeriod: PropTypes.func,
    formatWeekDays: PropTypes.func,
});
